"use client";

import { useRef, useState } from "react";
import { useSectionReveal } from "./useSectionReveal";

interface FAQItem {
  question: string;
  answer: string;
}

const FAQS: FAQItem[] = [
  {
    question: "How does the ordering process work?",
    answer:
      "It's simple! Choose a template, share your event details (names, dates, venue, photos) via WhatsApp, and we'll customize your invitation. You'll receive a preview within 1-2 days for your approval.",
  },
  {
    question: "Can I make changes after I receive the preview?",
    answer:
      "Absolutely. We offer unlimited revisions to make sure everything is perfect until you're 100% happy.",
  },
  {
    question: "How do guests RSVP through the invitation?",
    answer:
      "Each invitation has a built-in RSVP form. Guests simply fill in their name and attendance — you get real-time updates on who's coming, no phone calls needed.",
  },
  {
    question: "Will the invitation work on all phones?",
    answer:
      "Yes! Our invitations are fully responsive and tested on all devices — Android, iPhone, tablets, and desktops. They load fast and look beautiful everywhere.",
  },
  {
    question: "How do I share the invitation with guests?",
    answer:
      "You get a unique link that you can share directly on WhatsApp, Instagram, SMS, or email. Each guest can receive a personalized greeting with their name.",
  },
  {
    question: "Is there a limit on the number of guests?",
    answer:
      "No limits! Your invitation link can be shared with as many guests as you want. There's no per-guest charge — you pay once for the invitation.",
  },
  {
    question: "Do you offer refunds?",
    answer:
      "Since each invitation is custom-made, we don't offer refunds after work has begun. However, we work closely with you to ensure you're completely satisfied before delivery.",
  },
];

function AccordionItem({
  item,
  isOpen,
  onToggle,
}: {
  item: FAQItem;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const contentRef = useRef<HTMLDivElement>(null);

  return (
    <div className={`iv-faq__item ${isOpen ? "iv-faq__item--open" : ""}`}>
      <button className="iv-faq__question" onClick={onToggle}>
        <span>{item.question}</span>
        <svg
          className="iv-faq__chevron"
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M5 7.5l5 5 5-5" />
        </svg>
      </button>
      <div
        className="iv-faq__answer"
        ref={contentRef}
        style={{
          maxHeight: isOpen
            ? `${contentRef.current?.scrollHeight ?? 200}px`
            : "0px",
        }}
      >
        <p className="iv-faq__answer-text">{item.answer}</p>
      </div>
    </div>
  );
}

export default function FAQ() {
  const sectionRef = useSectionReveal<HTMLElement>();
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section className="iv-section iv-faq" id="faq" ref={sectionRef}>
      <div className="iv-section__inner">
        <div className="iv-heading">
          <h2 className="iv-heading__title iv-font-display">
            Frequently Asked Questions
          </h2>
          <p className="iv-heading__sub">
            Everything you need to know about our digital wedding invitations.
          </p>
        </div>

        <div className="iv-faq__list">
          {FAQS.map((faq, i) => (
            <AccordionItem
              key={faq.question}
              item={faq}
              isOpen={openIndex === i}
              onToggle={() => setOpenIndex(openIndex === i ? null : i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
